import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Chip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
  TablePagination,
  Tooltip,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import LocationOn from "@mui/icons-material/LocationOn";
import CloudOutlined from "@mui/icons-material/CloudOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import ChecklistRtl from "@mui/icons-material/ChecklistRtl";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import FolderOpenOutlined from "@mui/icons-material/FolderOpenOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { useGetOwnerAssetByIdQuery, useGetOwnerAssetInvestorsQuery } from "../../redux/api/authApi";
import { resolveInvestorUploadUrl } from "../investor/myInvestmentsMapper";
import { isRealEstateCategory } from "../../utils/assetCategory";
import { cleanLocation } from "../../utils/location";
import AssetOwnerImage from "./AssetOwnerImage";

const TEAL = "#00bfa5";
const TEAL_DARK = "#0d9488";

function mediaUrl(fileUrl) {
  if (!fileUrl) return "";
  const s = String(fileUrl).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return resolveInvestorUploadUrl(s);
}

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US").format(Math.round(x));
}

function formatAddress(addr) {
  if (!addr || typeof addr !== "object") return "";
  const parts = [
    addr.streetAddress,
    addr.city?.name,
    addr.state?.name,
    addr.country?.name,
    addr.zipCode,
  ].filter(Boolean);
  return parts.join(", ");
}

function documentTypeLabel(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("legal")) return "Legal";
  if (t.includes("financial") || t.includes("valuation")) return "Financial";
  if (t.includes("technical") || t.includes("spv")) return "Technical";
  return "Document";
}

function formatDocType(type) {
  const t = String(type || "").toLowerCase();
  if (t === "spvdocument" || t === "spv_Document") return "SPV Document";
  if (t === "regacertificate") return "REGA Certificate";
  if (t === "titledeed" || t === "title_deed") return "Title Deed";
  if (t === "legaldocument" || t === "legal_document") return "Legal Document";
  if (t === "valuationreport" || t === "valuation") return "Valuation Report";
  return type ? String(type).replace(/([A-Z])/g, " $1").trim() : "Document";
}

function verificationLabel(key) {
  const k = String(key).toLowerCase();
  if (k === "nafath") return { title: "Nafath Verification", subtitle: "Saudi National ID verification" };
  if (k === "vara") return { title: "VARA Verification", subtitle: "Virtual Assets Regulatory Authority" };
  if (k === "rega") return { title: "REGA Verification", subtitle: "Real Estate General Authority" };
  if (k === "taufeeq") return { title: "Valuation Report Verification", subtitle: "Asset documentation & compliance" };
  return { title: key, subtitle: "" };
}

function investmentStatusChip(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("complete")) {
    return { label: status || "Completed", sx: { bgcolor: "#dcfce7", color: "#166534", fontWeight: 700, borderRadius: "999px" } };
  }
  if (s.includes("active")) {
    return { label: status || "Active", sx: { bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700, borderRadius: "999px" } };
  }
  if (s.includes("pending") || s.includes("process")) {
    return { label: status || "Pending", sx: { bgcolor: "#ffedd5", color: "#c2410c", fontWeight: 700, borderRadius: "999px" } };
  }
  return {
    label: status || "—",
    sx: { bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700, borderRadius: "999px" },
  };
}

function formatInvestorDate(row) {
  if (row?.dateFormatted) return String(row.dateFormatted);
  if (!row?.date) return "—";
  const d = new Date(row.date);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function TruncatedCell({ value }) {
  const text = value == null || value === "" ? "—" : String(value);
  if (text === "—" || text.length <= 10) {
    return <span>{text}</span>;
  }
  return (
    <Tooltip title={text} arrow placement="top">
      <span style={{ cursor: "help" }}>{`${text.slice(0, 10)}…`}</span>
    </Tooltip>
  );
}

function formatNationalityDisplay(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).replace(/[_-]+/g, " ");
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const DETAIL_TABS = [
  { id: "overview", label: "Overview", Icon: ArticleOutlined },
  { id: "features", label: "Features", Icon: ChecklistRtl },
  { id: "specifications", label: "Specifications", Icon: TuneOutlined },
  { id: "location", label: "Location", Icon: PlaceOutlined },
  { id: "documents", label: "Documents", Icon: FolderOpenOutlined },
  { id: "verification", label: "Verification", Icon: VerifiedUserOutlined },
  { id: "investors", label: "Investors", Icon: GroupsOutlined },
];

const REAL_ESTATE_ONLY_TAB_IDS = new Set(["features", "specifications", "location"]);

export default function PropertyDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get("id")?.trim() || "";
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [detailTab, setDetailTab] = useState("overview");
  const [investorsPage, setInvestorsPage] = useState(1);
  const investorsLimit = 10;
  const detailTabsSectionRef = useRef(null);

  const { data: res, isLoading, isError, error, refetch } = useGetOwnerAssetByIdQuery(assetId, {
    skip: !assetId,
  });

  const {
    data: investorsRes,
    isLoading: investorsLoading,
    isFetching: investorsFetching,
    isError: investorsIsError,
    error: investorsError,
    refetch: refetchInvestors,
  } = useGetOwnerAssetInvestorsQuery(
    { assetId, page: investorsPage, limit: investorsLimit },
    { skip: !assetId || detailTab !== "investors" },
  );

  const investorsPayload = investorsRes?.data?.data ?? investorsRes?.data;
  const investorRows = Array.isArray(investorsPayload?.items) ? investorsPayload.items : [];
  const invPagination = investorsPayload?.pagination ?? {
    total: 0,
    page: investorsPage,
    limit: investorsLimit,
    totalPages: 1,
  };

  const asset = res?.data?.data ?? res?.data;

  const isRealEstate = useMemo(() => isRealEstateCategory(asset), [asset]);

  const mintTxHash = asset?.deployment?.mintTxHash || null;

  const visibleDetailTabs = useMemo(() => {
    const tabs = DETAIL_TABS.filter((t) => !REAL_ESTATE_ONLY_TAB_IDS.has(t.id) || isRealEstate);
    if (mintTxHash) {
      tabs.push({ id: "blockchain", label: "Blockchain", Icon: TagOutlined });
    }
    return tabs;
  }, [isRealEstate, mintTxHash]);

  useEffect(() => {
    setInvestorsPage(1);
  }, [assetId]);

  useEffect(() => {
    if (!isRealEstate && REAL_ESTATE_ONLY_TAB_IDS.has(detailTab)) {
      setDetailTab("overview");
    }
    if (detailTab === "blockchain" && !mintTxHash) {
      setDetailTab("overview");
    }
  }, [isRealEstate, detailTab, mintTxHash]);

  const thumbSources = useMemo(() => {
    if (!asset?.assetImages?.mainImage?.fileUrl) return [];
    const main = { key: "main", url: mediaUrl(asset.assetImages.mainImage.fileUrl) };
    const rest = (asset.assetImages.galleryImages || []).map((g, i) => ({
      key: `g-${i}`,
      url: mediaUrl(g.fileUrl),
    }));
    return [main, ...rest].filter((x) => x.url);
  }, [asset]);

  const mainUrl = useMemo(() => {
    if (!thumbSources.length) return "";
    const idx = Math.min(galleryIndex, thumbSources.length - 1);
    return thumbSources[idx]?.url || "";
  }, [thumbSources, galleryIndex]);

  const documentRows = useMemo(() => {
    if (!asset) return [];
    const rows = [];
    const seen = new Set();
    const push = (label, fileUrl, fileType, rawType, fileSize) => {
      const url = mediaUrl(fileUrl);
      if (!url || seen.has(url)) return;
      seen.add(url);
      rows.push({ label, url, fileType, rawType, fileSize});
    };

    (asset.documents || []).forEach((d) => {
      push(formatDocType(d.type), d.fileUrl, d.fileType, d.type, d.fileSize);
    });

    const fin = asset.financialDetails?.valuationReport;
    if (fin?.fileUrl) push("Valuation Report", fin.fileUrl, fin.fileType, "valuation", fin.fileSize);

    const doc = asset.documentation;
    if (doc?.titleDeed?.fileUrl) push("Title deed", doc.titleDeed.fileUrl, doc.titleDeed.fileType, doc.titleDeed.fileSize,"titleDeed");
    (doc?.legalDocuments || []).forEach((leg, i) => {
      push(`Legal document ${i + 1}`, leg.fileUrl, leg.fileType, leg.fileSize, "legalDocument");
    });

    return rows;
  }, [asset]);

  if (!assetId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Missing asset id. Open this page from My Listings.</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate("/asset/listings")}>
          Back to Listings
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  if (isError || !asset) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Could not load asset."}
        </Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate("/asset/listings")}>
          Back to Listings
        </Button>
      </Box>
    );
  }

  const basic = asset.basicInfo || {};
  const categoryName = asset.assetCategory?.name || "—";
  const locationLine = cleanLocation(asset.location) || formatAddress(basic.completeAddress);
  const inv = asset.investmentProgress || {};
  const tok = asset.tokenEconomics || {};
  const fin = asset.financialDetails || {};
  const totalSupply = Number(tok.totalSupply) || Number(inv.totalSupply) || 0;
  const tokensAvail = Number(inv.tokensAvailable) || 0;
  const tokensSold = Math.max(0, totalSupply - tokensAvail);
  const soldPct = totalSupply > 0 ? Math.min(100, (tokensSold / totalSupply) * 100) : 0;
  const investorsCount = Number(inv.currentInvestors) || 0;
  const features = Array.isArray(basic.featuresAndAmenities) ? basic.featuresAndAmenities : [];
  const luxury = basic.luxuryAssetDetails || {};
  const specs = basic.propertySpecifications || {};
  const isActive = String(asset.status || "").toLowerCase() === "active";

  const statusStr = asset.status || "Active";

  return (
    <Box sx={{ minHeight: "100vh", pb: 10 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/asset/listings")}
        sx={{ mb: 2, textTransform: "none", color: "#475569", fontWeight: 600 }}
      >
        Back to Listings
      </Button>

      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              {basic.assetName || "Asset"}
            </Typography>
            <Chip label={categoryName} size="small" sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700 }} />
          </Stack>
          {locationLine ? (
            <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ mt: 1 }}>
              <LocationOn sx={{ fontSize: 20, color: "#94a3b8" }} />
              <Typography variant="body2" color="text.secondary">
                {locationLine}
              </Typography>
            </Stack>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
          {statusStr !== 'Pending' && 
            <Chip
            icon={<CloudOutlined sx={{ fontSize: "18px !important" }} />}
            label="Deployed on Ethereum"
              size="small"
            variant="outlined"
            sx={{ bgcolor: "#fff", p: 1 }}
            />
          }
          <Chip
            label={statusStr}
            size="small"
            sx={{ bgcolor: isActive ? "#16a34a" : "#64748b", color: "#fff", fontWeight: 700 }}
          />
        </Stack>
      </Stack>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                height: { xs: 240, sm: 320, md: 380 },
                bgcolor: "#e2e8f0",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <AssetOwnerImage
                src={mainUrl}
                alt={basic.assetName || "Asset"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
            {thumbSources.length > 0 ? (
              <Stack direction="row" spacing={1.5} sx={{ p: 2, overflowX: "auto" }}>
                {thumbSources.map((t, idx) => (
                  <Box
                    key={t.key}
                    onClick={() => setGalleryIndex(idx)}
                    sx={{
                      width: 72,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      overflow: "hidden",
                      border: galleryIndex === idx ? `3px solid ${TEAL}` : "2px solid #e2e8f0",
                    }}
                  >
                    <AssetOwnerImage
                      src={t.url}
                      alt=""
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            ) : null}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Token availability
            </Typography>
            <Typography variant="body2" fontWeight={700} color="#0f172a" sx={{ mb: 1 }}>
              {formatTokens(tokensAvail)} of {formatTokens(totalSupply)} tokens available
            </Typography>
            <LinearProgress
              variant="determinate"
              value={soldPct}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: "#e2e8f0",
                mb: 2,
                "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: TEAL },
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ bgcolor: "#e8f8f5", borderRadius: 2, px: 2, py: 1.5, mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupsOutlined sx={{ color: TEAL_DARK }} />
                <Typography variant="body2" fontWeight={600}>
                  Current Investors
                </Typography>
              </Stack>
              <Typography fontWeight={800}>{investorsCount || "—"}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Minimum Investment
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ mb: 2 }}>
              SAR {formatSar(fin.minimumInvestment)}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setDetailTab("investors")}
              sx={{
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TEAL,
                "&:hover": { bgcolor: TEAL_DARK },
              }}
            >
              Manage investors
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setDetailTab("documents");
                requestAnimationFrame(() => {
                  detailTabsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
              sx={{
                mt: 1.5,
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                borderColor: "#cbd5e1",
                color: "#334155",
              }}
            >
              Manage documents
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block", lineHeight: 1.4 }}>
              Track funding, documents, and investor activity from this listing dashboard.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        {[
          { label: "Expected ROI", value: fin.expectedROI != null ? `${fin.expectedROI}%` : "—", bg: "#e8f4fc", color: "#0369a1" },
          { label: "Rental Yield", value: fin.rentalYield != null ? `${fin.rentalYield}%` : "—", bg: "#ecfdf5", color: "#047857" },
          {
            label: "Per Token",
            value: tok.tokenPriceUSD != null ? `SAR ${formatSar(tok.tokenPriceUSD)}` : "—",
            bg: "#f3e8ff",
            color: "#7c3aed",
          },
          { label: "Asset Value", value: `SAR ${formatSar(fin.totalAssetValue)}`, bg: "#fff7ed", color: "#c2410c" },
        ].map((cell) => (
          <Grid size={{ xs: 6, sm: 3 }} key={cell.label}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: cell.bg, border: "1px solid rgba(0,0,0,0.04)", height: "100%" }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: cell.color }}>
                {cell.label}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a", mt: 0.5 }}>
                {cell.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box ref={detailTabsSectionRef} sx={{ width: "100%" }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              {visibleDetailTabs.map(({ id, label, Icon }) => {
                const active = detailTab === id;
                return (
                  <Button
                    key={id}
                    fullWidth
                    onClick={() => setDetailTab(id)}
                    startIcon={<Icon sx={{ fontSize: 20 }} />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.5,
                      px: 2,
                      borderRadius: 0,
                      fontWeight: 600,
                      color: active ? TEAL_DARK : "#64748b",
                      bgcolor: active ? "rgba(0,191,165,0.12)" : "transparent",
                      borderLeft: active ? `4px solid ${TEAL}` : "4px solid transparent",
                      "&:hover": { bgcolor: active ? "rgba(0,191,165,0.16)" : "#f8fafc" },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e2e8f0", minHeight: 360 }}>
              {detailTab === "overview" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    About this Asset
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {basic.description || "No description provided."}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1 }}>
                    Investment Highlights
                  </Typography>
                  <Stack spacing={1.25}>
                    {[
                      fin.expectedROI != null && `Projected ${fin.expectedROI}% expected ROI`,
                      fin.minimumInvestment != null &&
                        `Fractional ownership from SAR ${formatSar(fin.minimumInvestment)} minimum`,
                    ]
                      .filter(Boolean)
                      .map((text, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: "#e2e8f0",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            bgcolor: "#fafafa",
                          }}
                        >
                          <CheckCircleOutline sx={{ color: TEAL, mt: 0.15 }} />
                          <Typography variant="body2">{text}</Typography>
                        </Paper>
                      ))}
                  </Stack>
                  {!fin.expectedROI && !fin.minimumInvestment ? (
                    <Typography variant="body2" color="text.secondary">
                      More highlights will appear as asset data is completed.
                    </Typography>
                  ) : null}
                </Stack>
              )}

              {detailTab === "features" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Features &amp; amenities
                  </Typography>
                  {features.length ? (
                    <Grid container spacing={1.5}>
                      {features.map((f, i) => {
                        const label = typeof f === "string" ? f : f?.name || "—";
                        return (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${label}-${i}`}>
                            <Paper
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "#e8f8f5",
                                border: "1px solid rgba(0,191,165,0.2)",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <CheckCircleOutline sx={{ color: TEAL }} />
                              <Typography fontWeight={600}>{label}</Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography color="text.secondary">No features listed.</Typography>
                  )}
                </Stack>
              )}

              {detailTab === "specifications" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    {isRealEstate ? "Property specifications" : "Specifications"}
                  </Typography>
                  {isRealEstate ? (
                    <>
                      <Grid container spacing={2}>
                        {[
                          { label: "Property type", value: specs.propertyType },
                          {
                            label: "Size",
                            value:
                              specs.sizeSqFt != null
                                ? `${Number(specs.sizeSqFt).toLocaleString()} ${specs.sizeUnit || "sq ft"}`
                                : null,
                          },
                        ]
                          .filter((x) => x.value)
                          .map((row) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={row.label}>
                              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                  {row.label}
                                </Typography>
                                <Typography fontWeight={700}>{row.value}</Typography>
                              </Paper>
                            </Grid>
                          ))}
                      </Grid>
                      {!specs.propertyType && specs.sizeSqFt == null ? (
                        <Typography color="text.secondary">No specifications provided.</Typography>
                      ) : null}
                    </>
                  ) : Object.keys(luxury).length === 0 ? (
                    <Typography color="text.secondary">No specification details for this asset type.</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {[
                        { label: "Brand", value: luxury.brand },
                        { label: "Model", value: luxury.model },
                        { label: "Serial number", value: luxury.serialNumber },
                        { label: "Manufactured year", value: luxury.manufacturedYear },
                        { label: "Condition", value: luxury.condition },
                      ]
                        .filter((x) => x.value)
                        .map((row) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={row.label}>
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                {row.label}
                              </Typography>
                              <Typography fontWeight={700}>{row.value}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                    </Grid>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Token supply: {formatTokens(totalSupply)} · Price per token (SAR):{" "}
                    {tok.tokenPriceUSD != null ? formatSar(tok.tokenPriceUSD) : "—"}
                  </Typography>
                </Stack>
              )}

              {detailTab === "location" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Location
                  </Typography>
                  {locationLine ? (
                    <Typography variant="body2" color="text.secondary">
                      {locationLine}
                    </Typography>
                  ) : null}
                  {basic.completeAddress ? (
                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>
                        Full address
                      </Typography>
                      <Typography variant="body2">{formatAddress(basic.completeAddress)}</Typography>
                      {basic.completeAddress.zipCode ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          ZIP: {basic.completeAddress.zipCode}
                        </Typography>
                      ) : null}
                    </Paper>
                  ) : null}
                </Stack>
              )}

              {detailTab === "documents" && (
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                    <Typography variant="h6" fontWeight={800}>
                      Asset Documents
                    </Typography>
                  </Stack>
                  {documentRows.length === 0 ? (
                    <Typography color="text.secondary">No documents available.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {documentRows.map((row) => (
                        <Paper
                          key={row.url}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <DescriptionOutlined sx={{ color: "#0284c7" }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={700}>{row.label}</Typography>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {/* <Chip label={documentTypeLabel(row.rawType)} size="small" variant="outlined" /> */}
                              {row.fileType ? (
                                <Typography variant="caption" color="text.secondary">
                                  {String(row.fileType).toUpperCase()}
                                </Typography>
                              ) : null}
                               {row?.fileSize
                                  ? row.fileSize >= 1024 * 1024
                                    ? <Typography variant="caption" color="text.secondary">· {(row.fileSize / (1024 * 1024)).toFixed(1)} MB</Typography>
                                    : <Typography variant="caption" color="text.secondary">· {Math.round(row.fileSize / 1024)} KB</Typography>
                                  : null}
                            </Stack>
                          </Box>
                          <Link href={row.url} target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
                            Open
                          </Link>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}

              {detailTab === "verification" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    KYC &amp; Compliance Verification
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verification status for this asset and related compliance checks.
                  </Typography>
                  {asset.verification &&
                    Object.entries(asset.verification)
                      .filter(([key]) => key.toLowerCase() !== "rega" || isRealEstate)
                      .map(([key, value]) => {
                        const { title, subtitle } = verificationLabel(key);
                        const status = value ?? "—";
                        const ok = String(status).toLowerCase() === "approved";
                        const isVara = key.toLowerCase() === "vara";
                        return (
                          <Paper
                            key={key}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box>
                              <Typography fontWeight={700}>{title}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {subtitle}
                              </Typography>
                            </Box>
                            <Chip
                              label={status}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: isVara && ok ? "#f3e8ff" : ok ? "#dcfce7" : "#f1f5f9",
                                color: isVara && ok ? "#7c3aed" : ok ? "#166534" : "#475569",
                              }}
                            />
                          </Paper>
                        );
                      })}
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#e0f2fe", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <InfoOutlined sx={{ color: "#0369a1" }} />
                    <Box>
                      <Typography fontWeight={700} color="#0c4a6e">
                        Complete verifications to unlock full trading capabilities
                      </Typography>
                      <Typography variant="caption" color="#0369a1" sx={{ display: "block", mt: 0.5 }}>
                        Status values come from the platform. Some checks may depend on asset type and jurisdiction.
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              )}

              {detailTab === "blockchain" && mintTxHash && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a" }}>
                      Blockchain
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      On-chain deployment details for this asset.
                    </Typography>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2.5, borderRadius: 2, borderColor: "#e2e8f0" }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <TagOutlined sx={{ color: TEAL_DARK, mt: 0.25, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Transaction Hash
                        </Typography>
                        <Typography
                          component="a"
                          href={`https://etherscan.io/tx/${mintTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          fontWeight={600}
                          fontSize={14}
                          sx={{ display: "block", wordBreak: "break-all", fontFamily: "monospace", color: TEAL_DARK, mt: 0.5, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                          {mintTxHash}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              )}

              {detailTab === "investors" && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a" }}>
                      Investors &amp; funding
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Manage and track investments in this asset.
                    </Typography>
                  </Box>

                  {investorsIsError ? (
                    <Alert severity="error" onClose={() => refetchInvestors()}>
                      {investorsError?.data?.message || investorsError?.error || "Could not load investors."}
                    </Alert>
                  ) : null}

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      overflow: "hidden",
                    }}
                  >
                    <Table size="medium" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          {["Investor name", "Amount (SAR)", "Tokens", "Payment", "Transaction ID", "Date", "Status"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                color: "#64748b",
                                fontSize: 11,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                borderBottom: "1px solid #e2e8f0",
                                py: 1.75,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {investorsLoading && investorRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                              <CircularProgress size={36} sx={{ color: TEAL }} />
                            </TableCell>
                          </TableRow>
                        ) : investorRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 8, color: "text.secondary" }}>
                              No investors recorded yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          investorRows.map((row) => {
                            const st = investmentStatusChip(row.status);
                            const nat = formatNationalityDisplay(row.investorNationality);
                            return (
                              <TableRow
                                key={row.investmentId || `${row.investorId}-${row.date}`}
                                hover
                                sx={{
                                  "&:last-child td": { borderBottom: 0 },
                                  opacity: investorsFetching ? 0.65 : 1,
                                  transition: "opacity 0.2s",
                                }}
                              >
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9", py: 2 }}>
                                  <Typography fontWeight={700} sx={{ color: "#0f172a" }}>
                                    {row.investorName || "—"}
                                  </Typography>
                                  <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                                    {row.investorEmail ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {row.investorEmail}
                                      </Typography>
                                    ) : null}
                                    {nat ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {nat}
                                        {row.investorStatus ? ` · ${row.investorStatus}` : ""}
                                      </Typography>
                                    ) : row.investorStatus ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {row.investorStatus}
                                      </Typography>
                                    ) : null}
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9", fontWeight: 600 }}>
                                  SAR {formatSar(row.amountSar)}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9", fontWeight: 600 }}>
                                  {row.tokensPurchased != null ? Number(row.tokensPurchased).toLocaleString() : "—"}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9", color: "text.secondary" }}>
                                  {row.paymentMethod || "—"}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9", color: "text.secondary" }}>
                                  <TruncatedCell value={row.transactionId} />
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9", color: "text.secondary" }}>
                                  {formatInvestorDate(row)}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #f1f5f9" }}>
                                  <Chip size="small" label={st.label} sx={st.sx} />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                    {Number(invPagination.total) > 0 ? (
                      <TablePagination
                        component="div"
                        count={Number(invPagination.total) || 0}
                        page={Math.max(0, (Number(invPagination.page) || investorsPage) - 1)}
                        onPageChange={(_, newPage) => setInvestorsPage(newPage + 1)}
                        rowsPerPage={investorsLimit}
                        rowsPerPageOptions={[investorsLimit]}
                        onRowsPerPageChange={() => {}}
                        sx={{
                          borderTop: "1px solid #e2e8f0",
                          bgcolor: "#fafafa",
                          "& .MuiTablePagination-toolbar": { minHeight: 52, px: 1 },
                        }}
                      />
                    ) : null}
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
